/**
 * Product Password Gate Component
 * Handles password verification for protected products.
 *
 * Supports two modes:
 * 1. Wrapper mode: product content lives inside this element via [data-product-content]
 * 2. Section mode: this element is a section that hides subsequent sibling sections
 *    on the page until the password is entered. Sibling sections are identified by
 *    [data-gated-section] or all .section-wrapper siblings following this component's
 *    closest .section-wrapper parent.
 */
class ProductPasswordGate extends HTMLElement {
  static COOKIE_PREFIX = 'protected_product_';
  static COOKIE_DAYS = 30;

  constructor() {
    super();
    this.productId = this.dataset.productId;
    this.hasPassword = this.dataset.hasPassword === 'true';
    this.form = this.querySelector('[data-password-form]');
    this.productContent = this.querySelector('[data-product-content]');
    this.passwordInput = this.querySelector('[data-password-input]');
    this.submitForm = this.querySelector('[data-password-submit]');
    this.errorMessage = this.querySelector('[data-password-error]');
    this.passwordHash = this.getPasswordHash();
    this.gatedSections = [];
  }

  connectedCallback() {
    if (!this.hasPassword) return;

    // Collect gated sections (sibling sections that come after the gate)
    this.gatedSections = this.getGatedSections();

    // Check if already unlocked via cookie
    if (this.isUnlocked()) {
      this.showProduct();
      return;
    }

    // Hide gated sections until password is entered
    this.hideSections();

    // Set up form submission
    this.submitForm?.addEventListener('submit', (e) => this.handleSubmit(e));
  }

  getPasswordHash() {
    const hashElement = this.querySelector('[data-password-hash]');
    if (!hashElement) return null;
    try {
      return JSON.parse(hashElement.textContent);
    } catch {
      return null;
    }
  }

  /**
   * Find sibling sections to gate. Looks for elements with [data-gated-section]
   * or all .section-wrapper siblings after this component's parent section-wrapper.
   */
  getGatedSections() {
    // First try explicit [data-gated-section] elements
    const explicit = document.querySelectorAll('[data-gated-section]');
    if (explicit.length > 0) return Array.from(explicit);

    // Fall back to sibling .section-wrapper elements after this one
    const parentSection = this.closest('.section-wrapper');
    if (!parentSection) return [];

    const sections = [];
    let sibling = parentSection.nextElementSibling;
    while (sibling) {
      if (sibling.classList.contains('section-wrapper')) {
        sections.push(sibling);
      }
      sibling = sibling.nextElementSibling;
    }
    return sections;
  }

  getCookieName() {
    return `${ProductPasswordGate.COOKIE_PREFIX}${this.productId}`;
  }

  isUnlocked() {
    const cookies = document.cookie.split(';');
    const cookieName = this.getCookieName();
    return cookies.some((cookie) => {
      const [name, value] = cookie.trim().split('=');
      return name === cookieName && value === 'unlocked';
    });
  }

  async hashPassword(password) {
    const encoder = new TextEncoder();
    const data = encoder.encode(password);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
  }

  setCookie() {
    const date = new Date();
    date.setTime(date.getTime() + ProductPasswordGate.COOKIE_DAYS * 24 * 60 * 60 * 1000);
    const expires = `; expires=${date.toUTCString()}`;
    document.cookie = `${this.getCookieName()}=unlocked${expires}; path=/; SameSite=Lax`;
  }

  async handleSubmit(e) {
    e.preventDefault();

    const enteredPassword = this.passwordInput?.value;
    if (!enteredPassword) return;

    const hashedInput = await this.hashPassword(enteredPassword);

    if (hashedInput === this.passwordHash) {
      this.setCookie();
      this.showProduct();
    } else {
      this.showError();
    }
  }

  hideSections() {
    this.gatedSections.forEach((section) => {
      section.hidden = true;
    });
  }

  showProduct() {
    // Hide the password form
    if (this.form) this.form.hidden = true;

    // Hide the entire gate section wrapper
    const sectionWrapper = this.closest('.section-wrapper');
    if (sectionWrapper) sectionWrapper.hidden = true;

    // Remove the server-side CSS that hides gated sections
    const gateStyle = document.getElementById('password-gate-style');
    if (gateStyle) gateStyle.remove();

    // Show inline product content (wrapper mode)
    if (this.productContent) this.productContent.hidden = false;

    // Show gated sibling sections (section mode)
    this.gatedSections.forEach((section) => {
      section.hidden = false;
    });
  }

  showError() {
    if (this.errorMessage) {
      this.errorMessage.hidden = false;
    }
    if (this.passwordInput) {
      this.passwordInput.value = '';
      this.passwordInput.focus();
    }
  }
}

if (!customElements.get('product-password-gate')) {
  customElements.define('product-password-gate', ProductPasswordGate);
}
